import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { PrintCompanyKitStruct, PrintCompanyKitItemStruct } from '../../../shared/services/responses/platform/admin-print-company-list.response';
import { UtcDatePipe, parseUtc } from '../sales-list/utc-date.pipe';

// IMPORTS PARA PDF
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

@Component({
  selector: 'app-admin-print-company-items-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, UtcDatePipe],
  template: `
    <div class="dlg">
      <h2>Itens da venda #{{ data?.saleNumber }}</h2>

      <div class="meta">
        <div>Data da venda: <strong>{{ data?.saleDateTime | utcDate }}</strong></div>
        <div *ngIf="data?.datetimeProductionStarted">Início da produção: <strong>{{ data?.datetimeProductionStarted | utcDate }}</strong></div>
        <div *ngIf="data?.sendDateTime">Enviado em: <strong>{{ data?.sendDateTime | utcDate }}</strong></div>
      </div>

      <!--dados do usuario comprador aqui-->
      <div class="meta" *ngIf="data.clientName">
        <div>Cliente: <strong>{{ data.clientName }}</strong></div>
      </div>

      <div class="meta" *ngIf="data.clientName">
        <div *ngIf="data.street || data.addressNumber">Endereço: 
          <strong>
            {{ data.zipcode }} | 
            {{ data.street }}{{ data.addressNumber ? ', ' + data.addressNumber : '' }} -
            {{ data.city }}{{ data.state ? ' / ' + data.state : '' }} 
          </strong>
        </div>
      </div>

      <div class="items-header">
        <div class="col q">Qtd</div>
        <div class="col t">Livro</div>
        <div class="col a">Ações</div>
      </div>

      <div *ngFor="let it of (data?.listPrintCompanyKitItemStruct || []); trackBy: trackItem" class="item-row">
        <div class="col q">{{ it.quantity }}</div>
        <div class="col t">
          <div class="title">{{ it.bookTitle }}</div>
          <div class="sub">({{ it.bookPageCount }} págs) - {{ it.bookPrintSize }} | {{ it.bookPrintDescription }}</div>
        </div>
        <div class="col a">
          <a class="btn" *ngIf="it.bookCoverUrl" [href]="it.bookCoverUrl" download>
            <mat-icon>image</mat-icon><span>Capa</span>
          </a>
          <a class="btn" *ngIf="it.bookPrintUrl" [href]="it.bookPrintUrl" download>
            <mat-icon>print</mat-icon><span>Miolo</span>
          </a>
          <span class="muted" *ngIf="!it.bookCoverUrl && !it.bookPrintUrl">Arquivos indisponíveis</span>
        </div>
      </div>

      <div class="dlg-actions">
        <button mat-stroked-button (click)="downloadResumoPdf()">
          <mat-icon>picture_as_pdf</mat-icon>
          Resumo do pedido (PDF)
        </button>

        <button mat-stroked-button (click)="close()">
          Fechar
        </button>
      </div>
    </div>
  `,
  styles: [`
    .dlg { padding: 20px; }
    h2 { margin: 0 0 10px; font-weight: 800; }
    .meta { color:#586174; font-size: 13px; display:flex; gap:16px; margin-bottom: 8px; flex-wrap: wrap; }
    .items-header, .item-row {
      display: grid; grid-template-columns: .3fr 1.6fr 1fr; gap: 8px; align-items: center;
    }
    .items-header { font-weight: 700; border-bottom: 1px solid #eee; padding: 8px 0; color:#6a7280; }
    .item-row { padding: 10px 0; border-bottom: 1px solid #f5f5f5; }
    .col.t .title { font-weight: 700; }
    .col.t .sub { font-size: 12px; color:#6a7280; }
    .col.a { display:flex; gap:8px; flex-wrap: wrap; }
    .btn { display:inline-flex; align-items:center; gap:6px; padding:6px 10px; border:1px solid #d3d6da; border-radius:10px; text-decoration:none; }
    .btn:hover { background:#f6f8fa; }
    .muted { color:#9aa2ad; font-size: 12px; }
    .dlg-actions { display:flex; justify-content:space-between; gap: 8px; margin-top: 14px; flex-wrap: wrap; }
  `]
})
export class AdminPrintCompanyItemsDialogComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PrintCompanyKitStruct,
    private ref: MatDialogRef<AdminPrintCompanyItemsDialogComponent>,
  ) { }

  trackItem = (_: number, it: PrintCompanyKitItemStruct) => it?.idKitPaymentItem ?? _;

  close() { this.ref.close(); }

  // ========= GERAR PDF RESUMO =========
  downloadResumoPdf(): void {
    if (!this.data) return;

    const doc = new jsPDF();

    const saleNumber = this.data.saleNumber ?? '';
    const title = `Resumo do pedido #${saleNumber}`;

    // Cabeçalho
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 14, 18);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');

    const saleDate = parseUtc(this.data.saleDateTime);
    const startDate = parseUtc(this.data.datetimeProductionStarted);
    const sendDate = parseUtc(this.data.sendDateTime);

    const fmt = (d: Date | null) =>
      d ? d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

    let y = 28;

    doc.text(`Data da venda: ${fmt(saleDate)}`, 14, y); y += 6;
    if (startDate) {
      doc.text(`Início da produção: ${fmt(startDate)}`, 14, y);
      y += 6;
    }
    if (sendDate) {
      doc.text(`Enviado em: ${fmt(sendDate)}`, 14, y);
      y += 6;
    }

    if (this.data.clientName) {
      doc.text(`Cliente: ${this.data.clientName}`, 14, y);
      y += 6;
    }

    const hasAddress = this.data.street || this.data.addressNumber || this.data.city || this.data.state || this.data.zipcode;
    if (hasAddress) {
      const addressParts: string[] = [];
      if (this.data.zipcode) addressParts.push(this.data.zipcode);
      if (this.data.street) {
        let street = this.data.street;
        if (this.data.addressNumber) {
          street += `, ${this.data.addressNumber}`;
        }
        addressParts.push(street);
      }
      if (this.data.city) {
        let city = this.data.city;
        if (this.data.state) {
          city += ` / ${this.data.state}`;
        }
        addressParts.push(city);
      }

      doc.text(`Endereço: ${addressParts.join(' | ')}`, 14, y);
      y += 8;
    } else {
      y += 4;
    }

    // Tabela de itens
    const items = this.data.listPrintCompanyKitItemStruct || [];

    const body = items.map(it => [
      it.quantity != null ? it.quantity.toString() : '',
      it.bookTitle || '',
      `${it.bookPrintSize || ''} | ${it.bookPrintDescription || ''}`
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Qtd', 'Livro', 'Formato']],
      body,
      styles: {
        fontSize: 10
      },
      headStyles: {
        fillColor: [240, 240, 240]
      },
      theme: 'grid'
    });

    // Total de livros
    const totalBooks = items.reduce((acc, it) => acc + (it.quantity || 0), 0);
    const finalY = (doc as any).lastAutoTable?.finalY || y;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total de livros: ${totalBooks}`, 14, finalY + 10);

    // Nome do arquivo
    const fileName = `resumo-pedido-${saleNumber || 'sem-numero'}.pdf`;
    doc.save(fileName);
  }
}
